import { NodeEditor, NodeEditorProps } from "@blings/node-lineup-creator";
import { IRootModel, useMst } from "../../stores/Root";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import _ from "lodash";
import { Button } from "antd";
import PublishConfirmDialog from "../../components/Modals/NodeManager/PublishConfirmDialog";
import "./FlowDiagramPage.scss";
import { BlingsButton } from "../../components/BlingsButton/BlingsButton";
import { AsyncOpState } from "../../types/enums/async-op-states";
import { getSnapshot } from "mobx-state-tree";
import { IFlowDiagram } from "@blings/blings-player";

const EMPTY_FLOW_DIAGRAM: IFlowDiagram = { nodes: [], edges: [] };
// 1.0.62 defers semantic node changes by 1000ms and exposes no flush method.
const NODE_EDITOR_CHANGE_SETTLE_MS = 1100;
export const FlowDiagramPage = observer(function FlowDiagramPage() {
  const store: IRootModel = useMst();
  const selectedProject = store.projectsStore.selectedProject!;
  const account = store.userStore.accountFromId(
    selectedProject.projectAccountId
  );
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [saveButtonOpState, setSaveButtonOpState] = useState<AsyncOpState>(
    AsyncOpState.Changed // Initialize to Changed to be enabled
  );

  // Use the MST model's view to get the transformed flow diagram
  const transformedFlowDiagram = useMemo(() => {
    if (!selectedProject.flowDiagramDraft) {
      return EMPTY_FLOW_DIAGRAM;
    }

    return selectedProject.flowDiagramDraft.asNodeEditorFlowDiagram;
  }, [selectedProject.flowDiagramDraft]);

  const dynamicDataSchema = useMemo<NodeEditorProps["dynamicDataSchema"]>(
    () => JSON.parse(selectedProject.stateJsonSchemaStr || "{}"),
    [selectedProject.stateJsonSchemaStr]
  );
  const liveControlSchema = useMemo<NodeEditorProps["liveControlSchema"]>(
    () => JSON.parse(selectedProject.settingsJsonSchemaStr || "{}"),
    [selectedProject.settingsJsonSchemaStr]
  );
  const overlay = useMemo<NodeEditorProps["overlay"]>(
    () =>
      selectedProject.overlay
        ? {
            audio: (selectedProject.overlay.audio || []).map(
              ({ url, volume, loop }) => ({
                url,
                volume: volume ?? undefined,
                loop: loop ?? undefined,
              })
            ),
          }
        : undefined,
    [selectedProject.overlay]
  );
  const currentFlowDiagramRef = useRef(transformedFlowDiagram);
  const activeProjectIdRef = useRef(selectedProject.id);
  activeProjectIdRef.current = selectedProject.id;
  useEffect(() => {
    currentFlowDiagramRef.current = transformedFlowDiagram;
  }, [selectedProject.id, transformedFlowDiagram]);

  // 1.0.62 emits partial project updates at runtime, despite declaring a full
  // project in its callback type. Keep graph edits without rerendering on drags.
  const onChange = useCallback<NonNullable<NodeEditorProps["onChange"]>>(
    (projectState) => {
      if (
        activeProjectIdRef.current === selectedProject.id &&
        projectState.flowDiagram
      ) {
        currentFlowDiagramRef.current = projectState.flowDiagram;
      }
    },
    [selectedProject.id]
  );

  // To clear timeouts if component unmounts
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveGenerationRef = useRef(0);
  useEffect(() => {
    setSaveButtonOpState(AsyncOpState.Changed);
    setIsPublishDialogOpen(false);
    return () => {
      saveGenerationRef.current += 1;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedProject.id]);
  async function saveFlow() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current); // Clear previous timeout if any
    }

    const saveGeneration = saveGenerationRef.current;
    setSaveButtonOpState(AsyncOpState.Saving);
    await new Promise<void>((resolve) =>
      setTimeout(resolve, NODE_EDITOR_CHANGE_SETTLE_MS)
    );
    if (saveGeneration !== saveGenerationRef.current) return;

    const currentFlowDiagram = currentFlowDiagramRef.current;
    // The store mutates the draft before saving; its published snapshot changes
    // only on success, so failed requests must remain retryable.
    const storeFlowDiagramSnapshot = selectedProject.flowDiagram
      ? getSnapshot(selectedProject.flowDiagram)
      : null;
    const deepCompareResult = _.isEqual(
      currentFlowDiagram,
      storeFlowDiagramSnapshot
    );
    if (deepCompareResult) {
      setSaveButtonOpState(AsyncOpState.Success); // Indicate "Saved" even if no change
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return currentFlowDiagram;
    }

    try {
      selectedProject.setFlowDiagramDraft(currentFlowDiagram);
      await selectedProject.saveFlowDiagramDraft();
      if (saveGeneration !== saveGenerationRef.current) return;
      setSaveButtonOpState(AsyncOpState.Success);
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return currentFlowDiagram;
    } catch (error) {
      if (saveGeneration !== saveGenerationRef.current) return;
      console.error("Failed to save flow diagram draft:", error);
      setSaveButtonOpState(AsyncOpState.Error);
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return; // Explicitly return undefined on error
    }
  }
  async function publishFlow() {
    const currentFlowDiagram = await saveFlow();
    if (currentFlowDiagram) {
      selectedProject.setFlowDiagram(currentFlowDiagram);
      await selectedProject.saveFlowDiagram();
    }
  }

  return (
    <div style={{ height: "80vh", width: "100vw" }} className="FlowDiagramPage">
      <div className="node-manager-header">
        <BlingsButton
          className={"save-button"}
          onClick={saveFlow}
          opState={saveButtonOpState}
        />
        <Button
          className="publish-button"
          disabled={saveButtonOpState === AsyncOpState.Saving}
          onClick={() => {
            setIsPublishDialogOpen(true);
          }}
        >
          Publish
        </Button>
      </div>
      <NodeEditor
        key={selectedProject.id}
        videoParts={selectedProject.publishedVideoParts}
        dynamicDataSchema={dynamicDataSchema}
        liveControlSchema={liveControlSchema}
        liveControl={selectedProject.settings || {}}
        accountFonts={account ? getSnapshot(account.fonts) : []}
        playerSettings={selectedProject.playerSettings || undefined}
        overlay={overlay}
        fontOverrides={selectedProject.fontOverrides || []}
        flowDiagram={transformedFlowDiagram}
        onChange={onChange}
      />

      <PublishConfirmDialog
        isOpen={isPublishDialogOpen}
        onClose={() => setIsPublishDialogOpen(false)}
        onConfirm={publishFlow}
      />
    </div>
  );
});
